'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useIdPath } from '@/contexts/IdPathProvider';
import { useLanguage } from '@/contexts/I18nProvider';
import minimapServices, { MinimapPolygon } from '@/templates/Heritage/utils/services/minimap';

const polygonCache = new Map<string, MinimapPolygon[]>();

type IdleDeadlineLike = {
    didTimeout: boolean;
    timeRemaining: () => number;
};

type RequestIdleLike = (
    callback: (deadline: IdleDeadlineLike) => void,
    options?: { timeout: number }
) => number;

interface PolygonShapeProps {
    polygon: MinimapPolygon;
    path: string;
    center: { x: number; y: number };
    isHovered: boolean;
    isIOSSafari: boolean;
    interactionEnabled: boolean;
    shouldRenderTextLabels: boolean;
    fontSize: number;
    onHoverChange: (polygonId: number | null) => void;
    onPolygonClick?: (polygon: MinimapPolygon, e: React.MouseEvent) => void;
    onPointerDown?: React.PointerEventHandler<SVGPathElement>;
    onPointerMove?: React.PointerEventHandler<SVGPathElement>;
    onPointerUp?: React.PointerEventHandler<SVGPathElement>;
    onPointerCancel?: React.PointerEventHandler<SVGPathElement>;
}

const PolygonShape = React.memo(
    ({
        polygon,
        path,
        center,
        isHovered,
        isIOSSafari,
        interactionEnabled,
        shouldRenderTextLabels,
        fontSize,
        onHoverChange,
        onPolygonClick,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
    }: PolygonShapeProps) => {
        const fillColor = isHovered ? polygon.color_fill_hover : polygon.color_fill;
        const fillOpacity = isHovered ? polygon.color_fill_alpha_hover : polygon.color_fill_alpha;
        const effectiveType = polygon.display_polygon_type || 'always';
        const showText = shouldRenderTextLabels && polygon.show_text === 1 && !!polygon.title
            && (effectiveType !== 'hover' || isHovered);

        return (
            <g>
                <path
                    d={path}
                    fill={fillColor}
                    fillOpacity={fillOpacity}
                    stroke={polygon.color_line}
                    strokeWidth={polygon.border_width}
                    vectorEffect="non-scaling-stroke"
                    className={`cursor-pointer ${
                        isHovered && polygon.style_hover === 'highlight' ? 'brightness-110' : ''
                    }`}
                    style={{
                        pointerEvents: interactionEnabled ? 'visiblePainted' : 'none',
                        touchAction: 'none',
                        filter: isHovered && polygon.style_hover === 'glow'
                            ? 'brightness(1.3) saturate(1.2)'
                            : undefined,
                    }}
                    onMouseEnter={isIOSSafari ? undefined : () => onHoverChange(polygon.id)}
                    onMouseLeave={isIOSSafari ? undefined : () => onHoverChange(null)}
                    onClick={interactionEnabled && onPolygonClick ? (e) => onPolygonClick(polygon, e) : undefined}
                    onPointerDown={interactionEnabled ? onPointerDown : undefined}
                    onPointerMove={interactionEnabled ? onPointerMove : undefined}
                    onPointerUp={interactionEnabled ? onPointerUp : undefined}
                    onPointerCancel={interactionEnabled ? onPointerCancel : undefined}
                />

                {showText && (
                    <text
                        x={center.x}
                        y={center.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize={fontSize}
                        fontWeight="bold"
                        textLength={Math.max((polygon.title?.length || 1) * fontSize * 0.55, 1)}
                        lengthAdjust="spacingAndGlyphs"
                        className="pointer-events-none select-none"
                        style={{
                            paintOrder: 'stroke fill',
                            stroke: 'rgba(0,0,0,0.65)',
                            strokeWidth: '0.6',
                            strokeLinejoin: 'round',
                        }}
                    >
                        {polygon.title}
                    </text>
                )}
            </g>
        );
    },
    (prev, next) => (
        prev.isHovered === next.isHovered
        && prev.interactionEnabled === next.interactionEnabled
        && prev.shouldRenderTextLabels === next.shouldRenderTextLabels
        && prev.isIOSSafari === next.isIOSSafari
        && prev.fontSize === next.fontSize
        && prev.path === next.path
        && prev.polygon === next.polygon
    )
);

PolygonShape.displayName = 'PolygonShape';

/**
 * The absolute rendered rect of the map image within its CSS container.
 * All values are in CSS pixels relative to the container top-left corner.
 */
export interface MapRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface MinimapPolygonOverlayProps {
    /** Current minimap ID */
    minimapId: number | null;
    /** Exact rendered rectangle of the KRPano map image inside the container. */
    mapRect: MapRect | null;
    /** Callback when polygon is clicked */
    onPolygonClick?: (polygon: MinimapPolygon) => void;
    /** DOM id of the krpano map element that receives pan/zoom pointer events */
    targetElementId?: string;
    /** Custom class name */
    className?: string;
    /** ID parameter to allow direct zero-latency DOM manipulation */
    overlayId?: string;

    /** Optional project access token for locked minimap APIs */
    accessToken?: string;
}

/**
 * MinimapPolygonOverlay — Renders SVG polygons perfectly aligned over the KRPano minimap.
 *
 * Design:
 *  - Polygon coordinates are stored as percentages 0-100 (the same space the
 *    admin painter uses with viewBox="0 0 100 100").
 *  - This overlay also uses viewBox="0 0 100 100" + preserveAspectRatio="none"
 *    so numeric values map 1-to-1 — no pixel conversion math needed.
 *  - The SVG is absolutely positioned and sized to match the *actual rendered*
 *    KRPano map image rect (mapRect), not the container dimensions.  This keeps
 *    the polygons glued to the image at any screen size, DevTools zoom level,
 *    or KRPano zoom/pan state.
 */
export function MinimapPolygonOverlay({
    minimapId,
    mapRect,
    onPolygonClick,
    targetElementId = 'minimap_pano',
    className = '',
    overlayId,
    accessToken,
}: MinimapPolygonOverlayProps) {
    const { idPath } = useIdPath();
    const { locale } = useLanguage();
    
    const [polygons, setPolygons] = useState<MinimapPolygon[]>([]);
    const [hoveredPolygonId, setHoveredPolygonId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [visibleCount, setVisibleCount] = useState(0);
    const [interactionEnabled, setInteractionEnabled] = useState(false);
    const gestureStateRef = useRef({
        pointerId: -1,
        startX: 0,
        startY: 0,
        moved: false,
    });
    const suppressClickUntilRef = useRef(0);
    const isIOSSafari = typeof navigator !== 'undefined'
        && /iP(hone|od|ad)/i.test(navigator.userAgent || '')
        && /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');
    const isIPhone = typeof navigator !== 'undefined' && /iPhone/i.test(navigator.userAgent || '');
    const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');
    // Progressive rendering and delayed interaction apply to both iPhone and Android
    // because both have weaker GPUs that struggle with immediate full SVG overlay
    const isMobileDevice = isIPhone || isAndroid;
    const shouldRenderTextLabels = !isIOSSafari;

    // Load polygons when minimap changes
    useEffect(() => {
        let isMounted = true;
        let fetchTimeout: ReturnType<typeof setTimeout> | null = null;

        const loadPolygons = async () => {
            if (!minimapId || !idPath) {
                setPolygons([]);
                return;
            }

            const authScope = accessToken ? 'authenticated' : 'public';
            const cacheKey = `${idPath}:${locale}:${minimapId}:${authScope}`;

            try {
                setIsLoading(true);
                const data = await minimapServices.getPolygonsByMinimapId(idPath, minimapId, locale, accessToken);
                if (!isMounted) {
                    return;
                }

                const nextPolygons = data || [];
                if (nextPolygons.length > 0) {
                    polygonCache.set(cacheKey, nextPolygons);
                } else {
                    polygonCache.delete(cacheKey);
                }
                setPolygons(nextPolygons);
            } catch (error) {
                console.error('Failed to load polygons:', error);
                if (isMounted) {
                    setPolygons([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        // iOS Safari is sensitive to heavy map+SVG work during map switch.
        // Delay polygon fetch a bit so base image and layout settle first.
        const delay = isIOSSafari ? 220 : 0;
        fetchTimeout = setTimeout(loadPolygons, delay);

        return () => {
            isMounted = false;
            if (fetchTimeout) {
                clearTimeout(fetchTimeout);
            }
        };
    }, [minimapId, idPath, locale, accessToken, isIOSSafari]);

    useEffect(() => {
        if (!polygons.length || !isMobileDevice) {
            setVisibleCount(polygons.length);
            return;
        }

        setVisibleCount(0);

        const idleWindow = window as Window & {
            requestIdleCallback?: RequestIdleLike;
            cancelIdleCallback?: (id: number) => void;
        };

        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let idleId: number | null = null;

        if (typeof idleWindow.requestIdleCallback === 'function') {
            idleId = idleWindow.requestIdleCallback(() => {
                setVisibleCount(polygons.length);
            }, { timeout: 300 });
        } else {
            timeoutId = setTimeout(() => {
                setVisibleCount(polygons.length);
            }, 80);
        }

        return () => {
            if (idleId !== null && typeof idleWindow.cancelIdleCallback === 'function') {
                idleWindow.cancelIdleCallback(idleId);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [polygons, isMobileDevice]);

    useEffect(() => {
        setInteractionEnabled(false);

        if (!minimapId || !mapRect?.width || !mapRect?.height) {
            return;
        }

        // iOS Safari needs more time (450ms) due to WebGL context switching.
        // Android needs a shorter delay (200ms) to let image decode finish.
        const delay = isIOSSafari ? 450 : isAndroid ? 200 : 0;
        const timer = setTimeout(() => {
            setInteractionEnabled(true);
        }, delay);

        return () => clearTimeout(timer);
    }, [minimapId, mapRect?.width, mapRect?.height, isIOSSafari]);

    const displayedPolygons = useMemo(() => {
        if (!polygons.length) return [];
        if (!isMobileDevice) return polygons;
        return polygons.slice(0, visibleCount);
    }, [polygons, isMobileDevice, visibleCount]);

    // Generate SVG path — coordinates are already 0-100 matching viewBox="0 0 100 100"
    const generatePath = useCallback((points: MinimapPolygon['points']) => {
        if (!points || points.length < 3) return '';
        const sortedPoints = [...points].sort((a, b) => a.point_order - b.point_order);
        return (
            sortedPoints
                .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
                .join(' ') + ' Z'
        );
    }, []);

    // Polygon label center in viewBox units (0-100)
    const getPolygonCenter = useCallback((points: MinimapPolygon['points']) => {
        if (!points || points.length < 3) return { x: 0, y: 0 };
        return {
            x: points.reduce((s, p) => s + p.x, 0) / points.length,
            y: points.reduce((s, p) => s + p.y, 0) / points.length,
        };
    }, []);

    const polygonMeta = useMemo(
        () => displayedPolygons.map((polygon) => ({
            polygon,
            path: generatePath(polygon.points),
            center: getPolygonCenter(polygon.points),
        })),
        [displayedPolygons, generatePath, getPolygonCenter]
    );

    /**
     * Find the actual available horizontal span inside the polygon at a given y level
     * by casting a horizontal ray and collecting edge intersections.
     */
    const getAvailableWidthAtCenter = useCallback(
        (points: MinimapPolygon['points'], cy: number): number => {
            if (!points || points.length < 3) return 0;
            let minX = Infinity;
            let maxX = -Infinity;
            for (let i = 0; i < points.length; i++) {
                const p1 = points[i];
                const p2 = points[(i + 1) % points.length];
                if ((p1.y <= cy && p2.y > cy) || (p2.y <= cy && p1.y > cy)) {
                    const t = (cy - p1.y) / (p2.y - p1.y);
                    const x = p1.x + t * (p2.x - p1.x);
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                }
            }
            if (minX === Infinity) {
                // Fallback to half bbox width when ray misses (e.g. all points co-linear)
                const xs = points.map(p => p.x);
                return (Math.max(...xs) - Math.min(...xs)) * 0.5;
            }
            return maxX - minX;
        },
        []
    );

    /**
     * Compute a dynamic fontSize so text fits the actual interior width of the polygon.
     * Uses ray-casting at the centroid Y to get real available width, not bbox width.
     */
    const getPolygonFontSize = useCallback(
        (points: MinimapPolygon['points'], title: string): number => {
            if (!points || points.length < 3 || !title) return 3;
            const center = getPolygonCenter(points);
            const availW = getAvailableWidthAtCenter(points, center.y);
            const ys = points.map(p => p.y);
            const availH = (Math.max(...ys) - Math.min(...ys)) * 0.4;
            // 0.55 viewBox units per character at fontSize=1
            const byWidth = (availW * 0.82) / (title.length * 0.55);
            return Math.min(Math.max(Math.min(byWidth, availH), 1.5), 6);
        },
        [getPolygonCenter, getAvailableWidthAtCenter]
    );

    const fontSizeMap = useMemo(() => {
        const map = new Map<number, number>();
        displayedPolygons.forEach((polygon) => {
            map.set(polygon.id, getPolygonFontSize(polygon.points, polygon.title || ''));
        });
        return map;
    }, [displayedPolygons, getPolygonFontSize]);

    // All polygon shapes are always rendered; display_polygon_type only controls text visibility.
    // (hover type: shape always visible, text only shows on hover)

    // Handle polygon click
    const handlePolygonClick = useCallback((polygon: MinimapPolygon, e: React.MouseEvent) => {
        if (Date.now() < suppressClickUntilRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        onPolygonClick?.(polygon);
    }, [onPolygonClick]);

    const forwardPointerToMap = useCallback((type: string, event: React.PointerEvent<SVGPathElement>) => {
        if (typeof document === 'undefined') {
            return;
        }

        const target = document.getElementById(targetElementId);
        if (!target) {
            return;
        }

        try {
            target.dispatchEvent(new PointerEvent(type, {
                bubbles: true,
                cancelable: true,
                pointerId: event.pointerId,
                pointerType: event.pointerType,
                isPrimary: event.isPrimary,
                buttons: event.buttons,
                clientX: event.clientX,
                clientY: event.clientY,
                screenX: event.screenX,
                screenY: event.screenY,
                ctrlKey: event.ctrlKey,
                altKey: event.altKey,
                shiftKey: event.shiftKey,
                metaKey: event.metaKey,
                pressure: event.pressure,
                width: event.width,
                height: event.height,
            }));
        } catch {
            const fallbackType = type === 'pointermove'
                ? 'mousemove'
                : type === 'pointerup' || type === 'pointercancel'
                    ? 'mouseup'
                    : 'mousedown';

            target.dispatchEvent(new MouseEvent(fallbackType, {
                bubbles: true,
                cancelable: true,
                buttons: event.buttons,
                clientX: event.clientX,
                clientY: event.clientY,
                screenX: event.screenX,
                screenY: event.screenY,
                ctrlKey: event.ctrlKey,
                altKey: event.altKey,
                shiftKey: event.shiftKey,
                metaKey: event.metaKey,
            }));
        }
    }, [targetElementId]);

    const handlePolygonPointerDown = useCallback((event: React.PointerEvent<SVGPathElement>) => {
        gestureStateRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            moved: false,
        };

        forwardPointerToMap('pointerdown', event);
    }, [forwardPointerToMap]);

    const handlePolygonPointerMove = useCallback((event: React.PointerEvent<SVGPathElement>) => {
        const gesture = gestureStateRef.current;
        if (gesture.pointerId !== event.pointerId) {
            return;
        }

        if (!gesture.moved) {
            const distance = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY);
            if (distance >= 8) {
                gesture.moved = true;
                suppressClickUntilRef.current = Date.now() + 220;
            }
        }

        forwardPointerToMap('pointermove', event);
    }, [forwardPointerToMap]);

    const finishPolygonPointer = useCallback((event: React.PointerEvent<SVGPathElement>, type: 'pointerup' | 'pointercancel') => {
        const gesture = gestureStateRef.current;
        if (gesture.pointerId === event.pointerId && gesture.moved) {
            suppressClickUntilRef.current = Date.now() + 220;
        }

        if (gesture.pointerId === event.pointerId) {
            gestureStateRef.current = {
                pointerId: -1,
                startX: 0,
                startY: 0,
                moved: false,
            };
        }

        forwardPointerToMap(type, event);
    }, [forwardPointerToMap]);

    const handlePolygonPointerUp = useCallback((event: React.PointerEvent<SVGPathElement>) => {
        finishPolygonPointer(event, 'pointerup');
    }, [finishPolygonPointer]);

    const handlePolygonPointerCancel = useCallback((event: React.PointerEvent<SVGPathElement>) => {
        finishPolygonPointer(event, 'pointercancel');
    }, [finishPolygonPointer]);

    // Handle polygon hover
    const handlePolygonHover = useCallback((polygonId: number | null) => {
        setHoveredPolygonId(polygonId);
    }, []);

    if (isLoading || polygons.length === 0 || !mapRect || mapRect.width <= 0 || mapRect.height <= 0) {
        return null;
    }

    return (
        <svg
            id={overlayId}
            className={className}
            // 0-100 coordinate space identical to the admin polygon painter.
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{
                // Positioned to exactly overlay the KRPano map image.
                // Left, Top, Width, Height are omitted from React state sync to avoid jitter.
                // They are directly mutated at 60fps in useKrpanoMap.ts bypassing React.
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: 5,
                overflow: 'visible',
                touchAction: 'none', // Prevent iOS Safari from intercepting touches on polygons
            }}
        >
            {polygonMeta.map(({ polygon, path, center }) => {
                const isHovered = hoveredPolygonId === polygon.id;

                return (
                    <PolygonShape
                        key={polygon.id}
                        polygon={polygon}
                        path={path}
                        center={center}
                        isHovered={isHovered}
                        isIOSSafari={isIOSSafari}
                        interactionEnabled={interactionEnabled}
                        shouldRenderTextLabels={shouldRenderTextLabels}
                        fontSize={fontSizeMap.get(polygon.id) ?? 3}
                        onHoverChange={handlePolygonHover}
                        onPolygonClick={handlePolygonClick}
                        onPointerDown={handlePolygonPointerDown}
                        onPointerMove={handlePolygonPointerMove}
                        onPointerUp={handlePolygonPointerUp}
                        onPointerCancel={handlePolygonPointerCancel}
                    />
                );
            })}
        </svg>
    );
}

export default MinimapPolygonOverlay;
